import { Component, Inject, ViewChild } from '@angular/core';
import { DateInputFillMode, DateInputRounded, DateInputSize } from '@progress/kendo-angular-dateinputs';
import { NotificationService } from 'src/app/core/services/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { FileStoreAPIService } from 'src/app/extensions/services/api/file-store-api.service';
import { isNotEmpty } from 'src/app/extensions/utilities';
import { SIGNAL_ITEM_KEY, SPECIAL_OFFER_STATUS } from 'src/app/extensions/constants/commonConstants';
import { EditorResizableOptions } from '@progress/kendo-angular-editor';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { NgForm } from '@angular/forms';
import { SPECIAL_OFFER_ROUTES } from 'src/app/core/constants/routes-constants';
import { ENV_CONFIG } from 'src/app/core/injection.token';

@Component({
  selector: 'app-special-offers',
  templateUrl: './campaign-description.component.html',
  styleUrls: ['./campaign-description.component.scss'],
  standalone: false,
})
export class CampaignDescriptionComponent {
  @ViewChild('form') public form: NgForm;
  private stateSignalItem: any;
  canUnloadByPassForm: boolean = false;

  public specialOffer: app.models.GetSpecialOfferModel = {
    title: '',
    campaignDocuments: [],
  } as app.models.GetSpecialOfferModel;
  specialOfferId: string;

  acceptExtensions: string = '';

  constructor(
    private readonly _globalService: GlobalService,
    private readonly _notificationService: NotificationService,
    private readonly _translateService: TranslateService,
    private readonly _fileStoreAPIService: FileStoreAPIService,
    private readonly _router: Router,
    private _activatedRoute: ActivatedRoute,
    @Inject(ENV_CONFIG) private _envConfig: env.EnvConfigurationData
  ) {
    this.stateSignalItem = this._globalService.getSignalItem(SIGNAL_ITEM_KEY.SPECIAL_OFFER_DATA);
    this.specialOfferId = this._activatedRoute.snapshot.params['specialOfferId'];

    if (isNotEmpty(this.stateSignalItem))
      this.specialOffer = this.stateSignalItem.data as app.models.GetSpecialOfferModel;
    else {
      this._globalService.setSignalItem(SIGNAL_ITEM_KEY.SPECIAL_OFFER_DATA, undefined);
      if (this.specialOfferId) {
        this._router.navigate([
          SPECIAL_OFFER_ROUTES.ADMIN_SPECIAL_OFFERS,
          SPECIAL_OFFER_ROUTES.PREVIEW,
          this.specialOfferId,
        ]);
      } else {
        this.firstStepRedirectHandler();
      }
    }
  }

  public fillModeOutline: DateInputFillMode = 'outline';
  public roundedLarge: DateInputRounded = 'large';
  public sizeLarge: DateInputSize = 'large';
  public resizable: boolean | EditorResizableOptions = {
    minHeight: 700,
    maxHeight: 1000,
  };
  specialOfferStatus = SPECIAL_OFFER_STATUS;

  uploadFileInStoreHandler(event: any, isAttachment: boolean) {
    let file = event.target.files[0] as File;
    let fileExtension = file.name.split('.').pop();
    event.target.value = null;

    const UPLOAD_ALLOWED_EXTENSIONS = isAttachment
      ? this._envConfig.ALLOWED_FILE_EXTENSIONS.DOCUMENTS
      : this._envConfig.ALLOWED_FILE_EXTENSIONS.IMAGES;

    this.acceptExtensions = UPLOAD_ALLOWED_EXTENSIONS.map(extension => '.' + extension.toLowerCase()).join(',');

    if (fileExtension == null || !UPLOAD_ALLOWED_EXTENSIONS.includes(fileExtension.toLowerCase())) {
      this._notificationService.showWarning(
        this._translateService.instant('specialOffers.wizard.campaignDescription.wrongExtension'),
        this._translateService.instant('common.warning')
      );
      return;
    }

    const allowedFileSizeinBytes = this._envConfig.ALLOWED_FILE_SIZE * 1000000;
    if (file.size > allowedFileSizeinBytes) {
      this._notificationService.showWarning(
        this._translateService.instant('common.fileSizeExceeded', { maxSize: this._envConfig.ALLOWED_FILE_SIZE }),
        this._translateService.instant('common.warning')
      );
      return;
    }

    this.uploadFile(file, isAttachment);
  }

  removeFileHandler(index: number): void {
    if (index == null) this.specialOffer.campaignImageDocument = null;
    else this.specialOffer.campaignDocuments.splice(index, 1);
  }

  redirectToPrevStep() {
    this.canUnloadByPassForm = true;
    this._router.navigate([
      SPECIAL_OFFER_ROUTES.ADMIN_SPECIAL_OFFERS,
      SPECIAL_OFFER_ROUTES.NEW,
      SPECIAL_OFFER_ROUTES.FINANCING_DETAILS,
      this.specialOfferId ?? '',
    ]);
  }

  nextStepHandler() {
    this.canUnloadByPassForm = true;
    this.stateSignalItem = {
      data: { ...this.stateSignalItem.data, ...this.specialOffer, isCampaignDescriptionFormDirty: this.form.dirty },
    };
    this._globalService.setSignalItem(SIGNAL_ITEM_KEY.SPECIAL_OFFER_DATA, this.stateSignalItem);
    this._router.navigate([
      SPECIAL_OFFER_ROUTES.ADMIN_SPECIAL_OFFERS,
      SPECIAL_OFFER_ROUTES.NEW,
      SPECIAL_OFFER_ROUTES.CLIENT_SEGMANTATION,
      this.specialOfferId ?? '',
    ]);
  }

  firstStepRedirectHandler = () => {
    this._router.navigate([
      SPECIAL_OFFER_ROUTES.ADMIN_SPECIAL_OFFERS,
      SPECIAL_OFFER_ROUTES.NEW,
      SPECIAL_OFFER_ROUTES.CAMPAIGN_DETAILS,
      this.specialOfferId ?? '',
    ]);
  };

  private uploadFile(file: File, isAttachment: boolean) {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    this._fileStoreAPIService.postFile(formData).subscribe((responseFileUpload: any) => {
      if (isNotEmpty(responseFileUpload)) {
        let generatedFilesName = Object.keys(responseFileUpload);

        const reader = new FileReader();
        reader.onload = (event: any) => {
          if (isAttachment) {
            this.specialOffer.campaignDocuments.push({
              documentName: responseFileUpload[generatedFilesName[0]],
              documentStoragePath: generatedFilesName[0],
              documentSize: event.total,
            } as app.models.SpecialOfferXDocument);
          } else {
            this.specialOffer.campaignImageDocument = {
              documentName: responseFileUpload[generatedFilesName[0]],
              documentStoragePath: generatedFilesName[0],
              documentSize: event.total,
            } as app.models.SpecialOfferXDocument;
          }
        };
        reader.readAsDataURL(file);
      }
    });
  }
}
